import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Shield, Lock } from 'lucide-react';
import { PaymentForm } from '../components/payment/PaymentForm';
import { OTPForm } from '../components/payment/OTPForm';
import { ProcessingAnimation } from '../components/payment/ProcessingAnimation';
type PaymentStep = 'details' | 'otp' | 'processing';
export function PaymentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // In a real app, this would come from the location state or a payment intent API
  const amount = location.state?.amount || 4500;
  const orderId = location.state?.orderId || `ORD-${Math.floor(100000 + Math.random() * 900000)}`;
  const [step, setStep] = useState<PaymentStep>('details');
  const [paymentStatus, setPaymentStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [paymentData, setPaymentData] = useState<any>(null);
  const handlePaymentSubmit = (data: any) => {
    setPaymentData(data);
    setStep('otp');
  };
  const handleOTPSubmit = (otp: string) => {
    setStep('processing');
    setPaymentStatus('processing');
    // Simulate API call
    setTimeout(() => {
      setPaymentStatus('success');
    }, 5000);
  };
  const handleCancel = () => {
    navigate('/checkout');
  };
  const handlePaymentComplete = () => {
    navigate('/order-confirmation', {
      state: {
        orderId,
        amount,
        paymentMethod: 'Credit/Debit Card',
        paymentStatus: 'Paid',
        receiptNo: `REC-${Math.floor(10000000 + Math.random() * 90000000)}`,
        date: new Date().toISOString(),
        cardLast4: paymentData?.cardNumber?.slice(-4) || '****'
      }
    });
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col font-sans" data-id="element-1414">
      {/* Paysys Labs Header */}
      <header className="bg-white border-b border-gray-200 py-4 px-6 shadow-sm" data-id="element-1415">
        <div className="max-w-3xl mx-auto flex justify-between items-center" data-id="element-1416">
          <div className="flex items-center space-x-2" data-id="element-1417">
            <img src="https://cdn.magicpatterns.com/uploads/wtigZ1ySqavxJHCJbfDDio/Paysys_Logo.png" alt="Paysys Labs" className="h-8 object-contain" data-id="element-1418" />
          </div>
          <div className="flex items-center text-sm text-gray-500" data-id="element-1419">
            <Shield className="w-4 h-4 mr-1 text-green-500" data-id="element-1420" />
            <span data-id="element-1421">Secure Checkout</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow flex items-center justify-center p-4 sm:p-6" data-id="element-1422">
        <div className="w-full max-w-md bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100" data-id="element-1423">
          {/* Progress Bar */}
          <div className="h-1 w-full bg-gray-100" data-id="element-1424">
            <div className="h-full bg-blue-600 transition-all duration-500 ease-in-out" style={{
            width: step === 'details' ? '33%' : step === 'otp' ? '66%' : '100%'
          }} data-id="element-1425" />
          </div>

          <div className="p-6 sm:p-8" data-id="element-1426">
            {step === 'details' && <>
                <div className="mb-6" data-id="element-1427">
                  <h1 className="text-2xl font-bold text-gray-900" data-id="element-1428">
                    Payment Details
                  </h1>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-1429">
                    Enter your card information to complete the purchase.
                  </p>
                </div>
                <PaymentForm onSubmit={handlePaymentSubmit} onCancel={handleCancel} totalAmount={amount} data-id="element-1430" />
              </>}

            {step === 'otp' && <OTPForm onSubmit={handleOTPSubmit} onCancel={handleCancel} data-id="element-1431" />}

            {step === 'processing' && <ProcessingAnimation status={paymentStatus} onComplete={handlePaymentComplete} data-id="element-1432" />}
          </div>

          {/* Footer inside card */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex justify-between items-center" data-id="element-1433">
            <div className="flex items-center text-xs text-gray-500" data-id="element-1434">
              <Lock className="w-3 h-3 mr-1" data-id="element-1435" />
              256-bit SSL Encryption
            </div>
            <div className="flex items-center space-x-2" data-id="element-1436">
              <img src="https://cdn.magicpatterns.com/uploads/8dFj1ebd13MwAe3XqFYNrR/Visa_Logo.png" alt="Visa" className="h-4 opacity-60" data-id="element-1437" />
              <img src="https://cdn.magicpatterns.com/uploads/qD9CTacyXTdwXXPtrCxZQw/MasterCard_Logo.png" alt="Mastercard" className="h-4 opacity-60" data-id="element-1438" />
              <img src="https://cdn.magicpatterns.com/uploads/8TcgwChg7bMUnkD6TfzVkn/UnionPay_Logo.png" alt="UnionPay" className="h-4 opacity-60" data-id="element-1439" />
              <img src="https://cdn.magicpatterns.com/uploads/u4UiZ3PWX5VH4BCbbcVNsE/PayPak_Logo.png" alt="PayPak" className="h-4 opacity-60" data-id="element-1440" />
              <img src="https://cdn.magicpatterns.com/uploads/oZFx4MBZmzvJA34NArst7p/RAAST_Logo.png" alt="RAAST" className="h-4 opacity-60" data-id="element-1441" />
            </div>
          </div>
        </div>
      </main>

      {/* Paysys Labs Footer */}
      <footer className="py-6 text-center" data-id="element-1442">
        <div className="flex items-center justify-center text-sm text-gray-500" data-id="element-1443">
          <span data-id="element-1444">Powered by</span>
          <img src="https://cdn.magicpatterns.com/uploads/wtigZ1ySqavxJHCJbfDDio/Paysys_Logo.png" alt="Paysys Labs" className="h-5 object-contain ml-2" data-id="element-1445" />
        </div>
        <p className="text-xs text-gray-400 mt-2" data-id="element-1446">
          © {new Date().getFullYear()} Paysys Labs. All rights reserved.
        </p>
      </footer>
    </div>;
}